import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ApplyJobComponent } from '../../../components/apply-job/apply-job';
import { FeatureSkillsComponent } from '../../../components/feature-skills/feature-skills';
import { FooterComponent } from '../../../components/footer-main/footer-main';
import { FooterDesktopComponent } from '../../../components/footer-desktop/footer-desktop';
import { HeaderComponent } from '../../../components/header-main/header-main';
import { HeaderDesktopComponent } from '../../../components/header-desktop/header-desktop';
import { SlugifyPipe } from '../../../components/slugify.pipe';
import { TypePipe } from '../../../components/type.pipe';
import { YoutubePlayerComponent } from '../../../components/youtube-player/youtube-player';
import { AuthService } from '../../../core/auth.service';
import { JobService } from '../../../core/job.service';
import { globals } from '../../../../environments/globals';
import { environment } from '../../../../environments/environment';

@Component({
    imports: [CommonModule, ApplyJobComponent, FeatureSkillsComponent, FooterComponent, FooterDesktopComponent, HeaderComponent, HeaderDesktopComponent, SlugifyPipe, TypePipe, YoutubePlayerComponent],
    selector: 'app-detail',
    styleUrls: ['./detail.page.scss'],
    templateUrl: './detail.page.html'
})
export class DetailPage implements OnInit {
  bucketURL: string = environment.firebase.bucketURL;
  slug: any;
  job: any;
  directory: any = globals.directory;
  directory_data: any;
  isApply: boolean = false;
  text: any;
  constructor(
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    public auth: AuthService, 
    private js: JobService,
    private metaService: Meta,
    private route: ActivatedRoute,
    private titleService: Title,
  ) { }

  ngOnInit() {
    this.slug = this.route.snapshot.paramMap.get('slug');
    this.js.jobDetails(this.slug).subscribe(data => {
      this.job = data[0];
      if(this.job.country) {
        this.directory_data = this.directory[this.job.country];
        this.titleService.setTitle(this.job.title + ' | ' + (this.job.location=='remote' ? 'Remote' : 'Onsite ' + this.job.suburb + ' ' + this.job.city) + ' ' + this.directory_data.name);
        this.metaService.updateTag(
          { name: 'description', property: 'og:description', content: this.job.title  + ' | ' + (this.job.location=='remote' ? 'Remote - Work from Anywhere' : 'Onsite ' + this.job.suburb + ' ' + this.job.city) + ' ' + this.directory_data.name + ' | ' + this.job.overview }
        )
        this.createStructuredData();
      }
      if(this.job.video) {
        this.videoStructuredData();
      }
    })
  }

  createStructuredData() {
    if (isPlatformBrowser(this.platformId)) {
      const script = <HTMLScriptElement> this.doc.head.querySelector("script[type='application/ld+json']")
      || this.doc.head.appendChild(this.doc.createElement("script"));
      if(this.job) {
        this.text = {
          "@context": "https://schema.org",
          "@type" : "JobPosting",
          "title" : this.job.title,
          "industry" : this.job.grouping,
          "description" : this.job.description,
          "datePosted" : this.job.posted,
          "validThrough" : new Date(this.job.unpublish),
          "employmentType" : this.job.type == 'C' ? "CONTRACTOR" : "PERMANENT", // FULL_TIME, PART_TIME, CONTRACTOR, TEMPORARY, INTERN, VOLUNTEER, PER_DIEM, OTHER 
          "hiringOrganization" : {
            "@type" : "Organization",
            "name" : this.job.company,
          },
          "baseSalary": {
            "@type": "MonetaryAmount",
            "currency": this.job.currency.toUpperCase(),
            "value": {
              "@type": "QuantitativeValue",
              "minValue": this.job.type == 'C' ? this.job.rate.lower : this.job.salary.lower,
              "maxValue": this.job.type == 'C' ? this.job.rate.upper : this.job.salary.upper,        
              "unitText": this.job.type == 'C' ? "HOUR" : "YEAR" // HOUR, DAY, WEEK, MONTH, YEAR
            }
          }
        }
        if(this.job.location == 'onsite') {
          this.text.jobLocation = {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": this.job.suburb,
              "addressLocality": this.job.city,
              "addressRegion": this.job.region,
              "addressCountry": this.job.country
            }
          }
        } else {
          this.text.jobLocationType = "TELECOMMUTE";
          this.text.applicantLocationRequirements = {
            "@type": "Country",
            "name": "GLOBAL"
          };
        }
        if(this.job.url && this.job.url !== '') {
          let url = new URL(this.job.url);
          this.text.directApply = true;
          this.text.hiringOrganization.sameAs = url.host;
        }
        if(this.job.contact && this.job.contact !== '') {
          this.text.applicationContact = this.job.contact;
        }
        if(this.job.reference && this.job.reference !== '') {
          this.text.identifier = {
            "@type": "PropertyValue",
            "name": "gigexchange",
            "value": this.job.reference
          };
        }
        if(this.job.logo && this.job.logo !== '') {
          this.text.hiringOrganization.logo = this.bucketURL + this.job.logo + '?alt=media';
        }
      }

      script.type = "application/ld+json";
      script.text = JSON.stringify(this.text);
    }
  }

  videoStructuredData() {
    if (isPlatformBrowser(this.platformId)) {
      const script = <HTMLScriptElement> this.doc.body.querySelector("script[type='application/ld+json']")
      || this.doc.body.appendChild(this.doc.createElement("script"));
      if(this.job) {
        this.text = {
          "@context": "https://schema.org",
          "@type" : "VideoObject",
          "name" : this.job.title,
          "description" : this.job.overview,
          "uploadDate" : this.job.posted,
          "embedUrl" : this.job.video
        }
      }
      if(this.job.video && this.job.video !== '') {
        this.text.thumbnailUrl = ["https://i.ytimg.com/vi_webp/" + this.job.video.slice(-11) + "/maxresdefault.webp"];
      }
      script.type = "application/ld+json";
      script.text = JSON.stringify(this.text);
    }
  }
}
